/* import __COLOCATED_TEMPLATE__ from './authentication-methods.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
interface AuthenticationMethodsArgs {
  settings: any;
  showAllErrors: boolean;
  scimSettings: any;
  onProvisioningMethodUpdate: (method: string) => void;
  samlBaseUrl: string;
  samlAccount: any;
}

export const AUTH_METHODS = {
  emailPassword: 'email_password',
  twoFa: 'email_password_otp',
  google: 'google_oauth2',
  saml: 'saml',
};

export default class AuthenticationMethodsComponent extends Component<AuthenticationMethodsArgs> {
  @service intl!: IntlService;
  @service declare appService: AppService;
  @service declare store: Store;

  get samlAccount() {
    return this.args.samlAccount;
  }

  get authMethods() {
    return this.args.settings.authorizedSignInMethods;
  }

  get emailPasswordEnabled(): boolean {
    return (
      this.authMethods.includes(AUTH_METHODS.emailPassword) ||
      this.authMethods.includes(AUTH_METHODS.twoFa)
    );
  }

  get twoFARequired(): boolean {
    return (
      this.authMethods.includes(AUTH_METHODS.twoFa) &&
      !this.authMethods.includes(AUTH_METHODS.emailPassword)
    );
  }

  set twoFARequired(value: boolean) {
    let newMethods = new Set<string>(this.authMethods);

    if (value) {
      newMethods.add(AUTH_METHODS.twoFa);
      newMethods.delete(AUTH_METHODS.emailPassword);
    } else {
      newMethods.add(AUTH_METHODS.emailPassword);
    }

    this.args.settings.set('authorizedSignInMethods', Array.from(newMethods));
  }

  get googleSignInEnabled(): boolean {
    return this.authMethods.includes(AUTH_METHODS.google);
  }

  get samlEnabled(): boolean {
    return this.authMethods.includes(AUTH_METHODS.saml);
  }

  get canEnableSaml() {
    return this.appService.app.canUseSamlSso;
  }

  get isLastEnabledMethod() {
    let enabledMethods = [
      this.emailPasswordEnabled,
      this.googleSignInEnabled,
      this.samlEnabled,
    ].filter(Boolean);

    return enabledMethods.length === 1;
  }

  get shouldShowSamlCertMigrationBanner(): boolean {
    return this.samlAccount?.certificateMigrationStatus?.needs_migration ?? false;
  }

  isMethodEnabled(method: string): boolean {
    return this.authMethods.includes(method);
  }

  private updateEmailPasswordMethods(newMethods: Set<string>): Set<string> {
    if (newMethods.has(AUTH_METHODS.emailPassword) || newMethods.has(AUTH_METHODS.twoFa)) {
      newMethods.delete(AUTH_METHODS.emailPassword);
      newMethods.delete(AUTH_METHODS.twoFa);
    } else {
      newMethods.add(AUTH_METHODS.emailPassword);
      newMethods.add(AUTH_METHODS.twoFa);
    }

    return newMethods;
  }

  private setAuthMethodsIfChanged(newMethods: Set<string>): void {
    let updatedMethods = Array.from(newMethods).sort();

    let originalMethods =
      this.args.settings.changedAttributes().authorizedSignInMethods?.[0] ??
      this.args.settings.authorizedSignInMethods;

    let originalSorted = originalMethods.slice().sort();
    let areEqual =
      updatedMethods.length === originalSorted.length &&
      updatedMethods.every((method, index) => method === originalSorted[index]);

    this.args.settings.set('authorizedSignInMethods', areEqual ? originalMethods : updatedMethods);
  }

  @action
  updateAuthMethods(method: string): void {
    if (this.isMethodEnabled(method) && this.isLastEnabledMethod) {
      return;
    }

    if (method === AUTH_METHODS.saml && !this.samlAccount) {
      let { id } = this.appService.app;
      this.args.settings.set(
        'samlAccount',
        this.store.createRecord('saml-account', { id, name: id }),
      );
    }

    let newMethods = new Set<string>(this.authMethods);
    if (method === AUTH_METHODS.emailPassword) {
      newMethods = this.updateEmailPasswordMethods(newMethods);
    } else {
      newMethods.has(method) ? newMethods.delete(method) : newMethods.add(method);
    }

    this.setAuthMethodsIfChanged(newMethods);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Security::AuthenticationMethods': typeof AuthenticationMethodsComponent;
    'new-settings/workspace/security/authentication-methods': typeof AuthenticationMethodsComponent;
  }
}
